<template>
  <AppLayout title="Visão geral">
    <div v-if="!overview" class="text-muted">Carregando...</div>

    <div v-else>
      <div class="row">
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-primary-subtle text-primary"><i class="bi bi-shop"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Clientes</h6>
              <h3 class="mb-0">{{ overview.clients.total }}</h3>
              <small class="text-muted">{{ overview.clients.active }} ativo(s) · {{ overview.engagement.active_7d }} venderam nos últimos 7d</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-success-subtle text-success"><i class="bi bi-graph-up-arrow"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Vendas dos clientes no mês</h6>
              <h3 class="mb-0">{{ formatter.currency(overview.sales_month_total) }}</h3>
              <small class="text-muted">Soma da operação de todas as lojas</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-warning-subtle text-warning"><i class="bi bi-card-checklist"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Planos cadastrados</h6>
              <h3 class="mb-0">{{ overview.plans.length }}</h3>
              <router-link :to="{ name: 'plans' }" class="small">Gerenciar planos</router-link>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-info-subtle text-info"><i class="bi bi-person-plus-fill"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Ações rápidas</h6>
              <router-link :to="{ name: 'clients' }" class="btn btn-primary btn-sm mt-1">Ver clientes</router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="overview.subscriptions_alert.expired || overview.subscriptions_alert.expiring_soon">
        <div class="col-12 mb-3">
          <div class="alert alert-warning d-flex align-items-center gap-2 mb-0" role="alert">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <div>
              <strong v-if="overview.subscriptions_alert.expired">{{ overview.subscriptions_alert.expired }} assinatura(s) vencida(s)</strong>
              <span v-if="overview.subscriptions_alert.expired && overview.subscriptions_alert.expiring_soon"> e </span>
              <strong v-if="overview.subscriptions_alert.expiring_soon">{{ overview.subscriptions_alert.expiring_soon }} vencendo nos próximos 7 dias</strong>
              — apenas um alerta, o acesso do cliente não é afetado.
              <router-link :to="{ name: 'clients' }" class="ms-1">Ver clientes</router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="overview.engagement.inactive_14d">
        <div class="col-12 mb-3">
          <div class="alert alert-info d-flex align-items-center gap-2 mb-0" role="alert">
            <i class="bi bi-moon-stars-fill"></i>
            <div>
              <strong>{{ overview.engagement.inactive_14d }} cliente(s) ativo(s)</strong> sem nenhuma venda nos últimos 14 dias — pode valer um contato antes de virar cancelamento.
              <router-link :to="{ name: 'clients' }" class="ms-1">Ver clientes</router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3">
          <h2 class="section-title">Vendas de todos os clientes (dia 1 até hoje)</h2>
          <div class="card">
            <div class="card-body">
              <canvas ref="salesChartCanvas" height="90"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <h2 class="section-title">Clientes por plano</h2>
          <div class="table-card">
            <table class="table">
              <thead>
                <tr>
                  <th>Plano</th>
                  <th>Clientes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="plan in overview.plans" :key="plan.id">
                  <td>{{ plan.name }}</td>
                  <td>{{ plan.clients_count }}</td>
                </tr>
                <tr v-if="!overview.plans.length">
                  <td colspan="2" class="text-center text-muted">Nenhum plano cadastrado ainda.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-md-6 mb-3">
          <h2 class="section-title">Maior faturamento no mês</h2>
          <div class="table-card">
            <table class="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Vendas no mês</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="client in overview.top_clients" :key="client.user_id">
                  <td>
                    <router-link :to="{ name: 'client-detail', params: { id: client.user_id } }">{{ client.name }}</router-link>
                  </td>
                  <td>{{ formatter.currency(client.total) }}</td>
                </tr>
                <tr v-if="!overview.top_clients.length">
                  <td colspan="2" class="text-center text-muted">Nenhuma venda registrada este mês.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h2 class="section-title mb-0">Saúde do servidor</h2>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="loadServerStats" :disabled="loadingServer">
              <i class="bi bi-arrow-clockwise me-1"></i>{{ loadingServer ? 'Atualizando...' : 'Atualizar' }}
            </button>
          </div>
          <p class="text-muted small mb-2">
            Hospedagem compartilhada não expõe todas as métricas do sistema operacional — os blocos abaixo mostram "não disponível neste ambiente" quando o PHP não tem permissão pra ler o dado.
          </p>
          <div class="row" v-if="server">
            <div class="col-md-3 mb-3">
              <div class="card stat-card h-100">
                <div class="card-body">
                  <div class="stat-card__icon bg-primary-subtle text-primary"><i class="bi bi-cpu-fill"></i></div>
                  <h6 class="card-subtitle text-muted mb-2">Load average</h6>
                  <template v-if="server.load_average">
                    <h3 class="mb-0">{{ server.load_average['1m'].toFixed(2) }}</h3>
                    <small class="text-muted">5m: {{ server.load_average['5m'].toFixed(2) }} · 15m: {{ server.load_average['15m'].toFixed(2) }}</small>
                  </template>
                  <span v-else class="text-muted small">Não disponível neste ambiente</span>
                </div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="card stat-card h-100">
                <div class="card-body">
                  <div class="stat-card__icon bg-success-subtle text-success"><i class="bi bi-memory"></i></div>
                  <h6 class="card-subtitle text-muted mb-2">Memória do host</h6>
                  <template v-if="server.memory">
                    <h3 class="mb-0">{{ server.memory.percent }}%</h3>
                    <small class="text-muted">{{ server.memory.used_mb }} / {{ server.memory.total_mb }} MB</small>
                  </template>
                  <span v-else class="text-muted small">Não disponível neste ambiente</span>
                </div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="card stat-card h-100">
                <div class="card-body">
                  <div class="stat-card__icon bg-warning-subtle text-warning"><i class="bi bi-hdd-fill"></i></div>
                  <h6 class="card-subtitle text-muted mb-2">Disco (cota da conta)</h6>
                  <template v-if="server.disk">
                    <h3 class="mb-0">{{ server.disk.used_percent }}%</h3>
                    <small class="text-muted">{{ server.disk.free_gb }} GB livres de {{ server.disk.total_gb }} GB</small>
                  </template>
                  <span v-else class="text-muted small">Não disponível neste ambiente</span>
                </div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="card stat-card h-100">
                <div class="card-body">
                  <div class="stat-card__icon bg-info-subtle text-info"><i class="bi bi-database-fill"></i></div>
                  <h6 class="card-subtitle text-muted mb-2">Conexões MySQL</h6>
                  <template v-if="server.database">
                    <h3 class="mb-0">{{ server.database.connections }}</h3>
                  </template>
                  <span v-else class="text-muted small">Não disponível neste ambiente</span>
                </div>
              </div>
            </div>
          </div>
          <p class="text-muted small" v-if="server">
            Memória do processo PHP nesta requisição: {{ server.php.memory_usage_mb }} MB (pico {{ server.php.memory_peak_mb }} MB, limite {{ server.php.memory_limit ?? '—' }}) · PHP {{ server.php.version }} · atualizado às {{ formatter.dateTime(server.generated_at) }}
          </p>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
<script>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import Chart from 'chart.js/auto';
import AppLayout from "@/components/AppLayout.vue";
import { stats } from "@/models/stats";
import formatter from "@/services/formatter";

export default {
  name: 'DashboardView',
  components: {
    AppLayout
  },
  setup() {
    const overview = ref(null);
    const server = ref(null);
    const loadingServer = ref(false);
    const Stats = stats();
    const salesChartCanvas = ref(null);
    let salesChart = null;

    async function loadServerStats() {
      loadingServer.value = true;
      server.value = await Stats.serverStats();
      loadingServer.value = false;
    }

    // Datas vêm como 'YYYY-MM-DD'; formatar localmente evita o `new
    // Date('YYYY-MM-DD')` interpretar como meia-noite UTC e exibir o dia
    // anterior em fusos atrás do UTC (ex.: Brasil).
    function dayLabel(isoDate) {
      const [, month, day] = isoDate.split('-');
      return `${day}/${month}`;
    }

    function renderSalesChart(salesByDay) {
      if (!salesChartCanvas.value) return;

      const labels = salesByDay.map(day => dayLabel(day.date));
      const totals = salesByDay.map(day => day.total);

      if (salesChart) {
        salesChart.data.labels = labels;
        salesChart.data.datasets[0].data = totals;
        salesChart.update();
        return;
      }

      salesChart = new Chart(salesChartCanvas.value, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Vendas',
            data: totals,
            borderColor: '#6f42c1',
            backgroundColor: 'rgba(111, 66, 193, 0.15)',
            tension: 0.2,
            fill: true,
          }],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (context) => formatter.currency(context.parsed.y),
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: (value) => formatter.currency(value),
              },
            },
          },
        },
      });
    }

    onMounted(async () => {
      overview.value = await Stats.overview();
      if (overview.value?.sales_by_day) {
        // O canvas só existe no DOM depois que o v-else troca de lugar com
        // "Carregando...", o que só acontece no próximo tick após `overview`
        // mudar — sem isso, o canvas ainda é null aqui.
        await nextTick();
        renderSalesChart(overview.value.sales_by_day);
      }
      loadServerStats();
    });

    onBeforeUnmount(() => {
      salesChart?.destroy();
    });

    return { overview, formatter, salesChartCanvas, server, loadingServer, loadServerStats }
  },
}
</script>
<style scoped>
.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-strong);
  margin-bottom: 0.75rem;
}

.stat-card__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  margin-bottom: 0.75rem;
}
</style>
